<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { executeImportSession, getImportSession } from '@/api/api.js'
import { useRouter } from 'vue-router'
import MdiCheckCircle from '~icons/mdi/check-circle'
import MdiAlertCircle from '~icons/mdi/alert-circle'
import MdiDatabaseImport from '~icons/mdi/database-import'
import MdiAlertOctagon from '~icons/mdi/alert-octagon'

const props = defineProps({
  session: { type: Object, required: true }
})
const emit = defineEmits(['restart', 'back-to-preview'])

const router = useRouter()
const { t } = useI18n()
const confirmed = ref(false)
const executing = ref(false)
const polling = ref(false)
const pollingInterval = ref(null)
const currentSession = ref({ ...props.session })
const startError = ref('')
const startErrors = ref([])
const pollError = ref('')

const status = computed(() => currentSession.value.status)
const result = computed(() => currentSession.value.result_summary)
const isDone = computed(() => status.value === 'done')
const isFailed = computed(() => status.value === 'failed')
const isImporting = computed(() => status.value === 'importing')

const successCount = computed(() => result.value?.instances_created ?? 0)
const itemCount = computed(() => result.value?.items_created ?? 0)
const errors = computed(() => result.value?.errors ?? [])
const hasErrors = computed(() => errors.value.length > 0)

onMounted(() => {
  // Resume polling if already in progress when returning to this step
  if (isImporting.value) {
    confirmed.value = true
    startPolling()
  } else if (isDone.value || isFailed.value) {
    confirmed.value = true
  }
})

onUnmounted(() => {
  if (pollingInterval.value) clearInterval(pollingInterval.value)
})

async function kickOff() {
  confirmed.value = true
  executing.value = true
  startError.value = ''
  startErrors.value = []
  try {
    await executeImportSession(props.session.id)
    startPolling()
  } catch (err) {
    startError.value = err?.data?.error || t('import.cannotStartError')
    startErrors.value = err?.data?.errors || []
    executing.value = false
  }
}

function startPolling() {
  if (pollingInterval.value) clearInterval(pollingInterval.value)
  pollError.value = ''
  executing.value = false
  polling.value = true
  pollingInterval.value = setInterval(async () => {
    try {
      const res = await getImportSession(props.session.id)
      currentSession.value = res
      if (isDone.value || isFailed.value) {
        clearInterval(pollingInterval.value)
        polling.value = false
      }
    } catch {
      clearInterval(pollingInterval.value)
      polling.value = false
      pollError.value = t('import.importStatusCheckError')
    }
  }, 2000)
}
</script>

<template>
  <div class="flex flex-col gap-8 items-center text-center">
    <!-- Confirmation warning (shown before import starts) -->
    <template v-if="!confirmed">
      <div class="flex flex-col items-center gap-6 py-6 w-full max-w-md">
        <div
          class="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-6 flex flex-col items-center gap-4 w-full"
        >
          <MdiAlertOctagon class="w-12 h-12 text-amber-500" />
          <h2 class="text-(--color-text) text-lg font-semibold">{{ $t('import.readyToImport') }}</h2>
          <p class="text-(--color-muted) text-sm leading-relaxed">
            {{ $t('import.aboutToCreate') }}
            <strong class="text-(--color-text)">{{ $t('import.importCantUndo') }}</strong>
            {{ $t('import.importIrreversible') }}
          </p>
          <p class="text-(--color-muted) text-sm">{{ $t('import.goBackToMakeChanges') }}</p>
        </div>
        <button class="btn btn-primary flex items-center gap-2" @click="kickOff">
          <MdiDatabaseImport class="w-4 h-4" />
          {{ $t('import.startImport') }}
        </button>
      </div>
    </template>

    <!-- Loading / executing -->
    <template v-else-if="pollError">
      <div class="flex flex-col items-center gap-3 py-8" role="alert">
        <MdiAlertCircle class="w-10 h-10 text-red-400" />
        <p class="text-(--color-text)">{{ pollError }}</p>
        <button class="btn btn-primary" @click="startPolling">{{ $t('import.tryAgain') }}</button>
      </div>
    </template>

    <template v-else-if="executing || isImporting || polling">
      <div class="flex flex-col items-center gap-4 py-8">
        <span
          class="inline-block w-12 h-12 rounded-full border-4 border-(--color-accent) border-t-transparent animate-spin"
        />
        <p class="text-(--color-text) font-medium">{{ $t('import.importingEquipment') }}</p>
        <p class="text-(--color-muted) text-sm">{{ $t('import.mayTakeMoment') }}</p>
      </div>
    </template>

    <!-- Error starting import -->
    <template v-else-if="startError">
      <div class="flex flex-col items-center gap-4 py-8">
        <MdiAlertCircle class="w-12 h-12 text-red-400" />
        <p class="text-(--color-text) font-medium">{{ $t('import.couldNotStart') }}</p>
        <p class="text-red-400 text-sm">{{ startError }}</p>
        <ul v-if="startErrors.length" class="w-full max-w-md text-left text-red-400 text-sm list-disc pl-6">
          <li v-for="(issue, index) in startErrors" :key="`${issue.row_index}-${issue.field}-${index}`">
            {{ issue.row_index == null ? $t('import.preImportCheckLabel') : $t('import.rowPreview', { n: issue.row_index + 1 }) }}: {{ issue.field }}: {{ issue.message }}
          </li>
        </ul>
        <button class="btn btn-ghost" @click="emit('back-to-preview')">{{ $t('import.backToPreviewFix') }}</button>
        <button class="btn btn-primary" @click="kickOff">{{ $t('import.tryAgain') }}</button>
      </div>
    </template>

    <!-- Failed -->
    <template v-else-if="isFailed">
      <div class="flex flex-col items-center gap-4 py-4 w-full">
        <MdiAlertCircle class="w-12 h-12 text-red-400" />
        <p class="text-(--color-text) font-medium text-lg">{{ $t('import.importFailed') }}</p>
        <p v-if="currentSession.error_message" class="text-red-400 text-sm">
          {{ currentSession.error_message }}
        </p>

        <!-- Per-row errors -->
        <div v-if="hasErrors" class="w-full">
          <p class="text-(--color-muted) text-sm mb-3">
            {{ errors.length === 1 ? $t('import.issueCountSingular', { count: errors.length }) : $t('import.issueCountPlural', { count: errors.length }) }}
          </p>
          <div class="list-stack">
            <div
              v-for="err in errors"
              :key="`${err.row_index}-${err.field}`"
              class="data-card text-left"
            >
              <div class="data-card__main">
                <p class="data-card__name text-sm">
                  {{ err.row_index != null ? $t('import.rowPreview', { n: err.row_index + 1 }) : $t('import.preImportCheckLabel') }}
                </p>
                <p class="data-card__meta text-red-400">
                  <span v-if="err.field && err.field !== '__row__'" class="font-medium"
                    >{{ err.field }}:
                  </span>
                  {{ err.message }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="flex gap-3 flex-wrap justify-center pt-2">
          <button class="btn btn-primary" @click="emit('back-to-preview')">{{ $t('import.backToPreviewFix') }}</button>
          <button class="btn btn-ghost" @click="emit('restart')">{{ $t('import.startNewImport') }}</button>
        </div>
      </div>
    </template>

    <!-- Done -->
    <template v-else-if="isDone">
      <!-- Success summary -->
      <div class="flex flex-col items-center gap-2 py-4">
        <MdiCheckCircle class="w-14 h-14 text-(--color-accent)" />
        <h2 class="text-(--color-text) text-xl font-semibold">{{ $t('import.importComplete') }}</h2>
      </div>

      <!-- Stats -->
      <div class="grid grid-cols-2 gap-4 w-full max-w-sm">
        <div
          class="rounded-xl border border-(--color-border) bg-(--color-surface) p-4 flex flex-col items-center gap-1"
        >
          <span class="text-3xl font-bold text-(--color-accent)">{{ itemCount }}</span>
          <span class="text-(--color-muted) text-sm">{{ $t('import.itemsCreated') }}</span>
        </div>
        <div
          class="rounded-xl border border-(--color-border) bg-(--color-surface) p-4 flex flex-col items-center gap-1"
        >
          <span class="text-3xl font-bold text-(--color-accent)">{{ successCount }}</span>
          <span class="text-(--color-muted) text-sm">{{ $t('import.instancesCreated') }}</span>
        </div>
      </div>

      <!-- Errors -->
      <div v-if="hasErrors" class="w-full">
        <div class="flex items-center gap-2 mb-3">
          <MdiAlertCircle class="w-5 h-5 text-red-400" />
          <span class="text-red-400 font-medium">{{ errors.length === 1 ? $t('import.rowsNotImportedSingular', { count: errors.length }) : $t('import.rowsNotImportedPlural', { count: errors.length }) }}</span>
        </div>
        <div class="list-stack">
          <div
            v-for="err in errors"
            :key="`${err.row_index}-${err.field}`"
            class="data-card text-left"
          >
            <div class="data-card__main">
              <p class="data-card__name text-sm">{{ $t('import.rowPreview', { n: (err.row_index ?? 0) + 1 }) }}</p>
              <p class="data-card__meta text-red-400">
                <span v-if="err.field && err.field !== '__row__'" class="font-medium"
                  >{{ err.field }}:
                </span>
                {{ err.message }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex gap-3 flex-wrap justify-center">
        <button class="btn btn-primary flex items-center gap-2" @click="router.push('/items')">
          <MdiDatabaseImport class="w-4 h-4" />
          {{ $t('import.goToItems') }}
        </button>
        <button class="btn btn-ghost" @click="emit('restart')">{{ $t('import.importAnother') }}</button>
      </div>
    </template>
  </div>
</template>
